import React from 'react';
import { View, Text, StyleSheet, Pressable, ScrollView } from 'react-native';
import { Image } from 'expo-image';
import { Camera, ChevronRight } from 'lucide-react-native';
import { router } from 'expo-router';
import { useTheme, SPACING, BORDER_RADIUS, FONT_SIZES, TYPOGRAPHY } from '../../theme';
import { Card, Avatar } from '../common';
import { Post } from '../../types';
import { formatRelativeTime } from '../../utils/time';
import { PendingObservations } from '../feed/PendingObservations';

interface RecentActivitySectionProps {
  structurePosts: Post[];
  postsCount: number;
  structureId: string;
  /** True once the structure-posts fetch for THIS structure has completed
   *  (loaded or legitimately empty). Surfaced to the DOM so the visual audit
   *  can wait for a resolved screen instead of shooting mid-load. */
  postsResolved?: boolean;
}

function ActivityRow({ post, onPress }: { post: Post; onPress: () => void }) {
  const { colors } = useTheme();
  return (
    <Pressable onPress={onPress} style={({ pressed }) => [styles.row, pressed && { opacity: 0.6 }]}>
      <Avatar size={28} name={post.authorName} imageUrl={post.authorAvatarUrl} />
      <View style={styles.rowBody}>
        <Text style={[styles.caption, { color: colors.text }]} numberOfLines={2}>
          {post.caption}
        </Text>
        {/* textSecondary, not textTertiary: an author + timestamp line is
            content, and tertiary at 12px on white measures 2.72:1 against
            the 4.5:1 AA floor — axe caught this the day the demo post's
            relative age first rendered this row (same rule as CommentItem). */}
        <Text style={[styles.meta, { color: colors.textSecondary }]} numberOfLines={1}>
          {post.authorName} · {formatRelativeTime(post.createdAt, { longFallbackAfterWeeks: 5 })}
        </Text>
      </View>
    </Pressable>
  );
}

export function RecentActivitySection({
  structurePosts,
  postsCount,
  structureId,
  postsResolved,
}: RecentActivitySectionProps) {
  const { colors } = useTheme();
  const visible = structurePosts.slice(0, 4);
  const photoPosts = structurePosts.filter((p) => p.thumbnailUrl || p.imageUrl).slice(0, 4);
  // postsCount comes with the structure doc, the list from a later fetch.
  // While that fetch is out, an empty list means "not here yet", so it
  // mustn't say "No observations yet" under a "18 posts" link.
  const waitingForPosts = postsResolved === false && visible.length === 0 && postsCount > 0;

  // Everything below the "Activity" title is live Firestore content — which
  // posts exist on this structure changes day to day, and per-item grey-box
  // masks still leak the NUMBER and POSITION of boxes. So each data-varying
  // container (count link, photo strip, row list, empty state) carries
  // data-visualhide, and the visual audit's CSS removes the whole container:
  // the shot is identical whether the structure has 0 posts or 40.
  return (
    <>
      <PendingObservations structureId={structureId} />
      {postsResolved && <View testID="structure-activity-resolved" style={styles.resolvedMarker} />}
      <Card variant="outlined" padding="md" style={styles.card}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: colors.text }]}>Activity</Text>
          {postsCount > 0 && (
            <Pressable
              onPress={() => router.push(`/structure/threads?id=${structureId}`)}
              style={({ pressed }) => [styles.viewAll, pressed && { opacity: 0.6 }]}
              // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
              dataSet={{ visualhide: 'true' }}>
              <Text style={[styles.viewAllText, { color: colors.textSecondary }]}>
                {postsCount} {postsCount === 1 ? 'post' : 'posts'}
              </Text>
              <ChevronRight size={14} color={colors.textTertiary} />
            </Pressable>
          )}
        </View>

        {photoPosts.length > 0 && (
          <View
            // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
            dataSet={{ visualhide: 'true' }}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.photoStrip}>
              {photoPosts.map((p) => (
                <Pressable
                  key={p.id}
                  onPress={() => router.push(`/post/${p.id}`)}
                  style={({ pressed }) => pressed && { opacity: 0.7 }}>
                  <Image
                    source={p.thumbnailUrl || p.imageUrl}
                    style={[styles.photoThumb, { backgroundColor: colors.surface }]}
                    contentFit="cover"
                    cachePolicy="memory-disk"
                    transition={150}
                  />
                </Pressable>
              ))}
            </ScrollView>
          </View>
        )}

        {waitingForPosts ? (
          <View style={styles.list} testID="structure-activity-loading">
            {[0, 1].map((i) => (
              <View key={i} style={styles.loadingRow}>
                <View style={[styles.loadingThumb, { backgroundColor: colors.skeletonBase }]} />
                <View style={[styles.loadingLine, { backgroundColor: colors.skeletonBase }]} />
              </View>
            ))}
          </View>
        ) : visible.length > 0 ? (
          <View
            style={styles.list}
            // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
            dataSet={{ visualhide: 'true' }}>
            {visible.map((post, idx) => (
              <View
                key={post.id}
                style={[
                  idx > 0 && {
                    borderTopWidth: StyleSheet.hairlineWidth,
                    borderTopColor: colors.borderLight,
                  },
                ]}>
                <ActivityRow post={post} onPress={() => router.push(`/post/${post.id}`)} />
              </View>
            ))}
          </View>
        ) : (
          <View
            style={[styles.empty, { backgroundColor: colors.surface }]}
            // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
            dataSet={{ visualhide: 'true' }}>
            <Camera size={22} color={colors.textTertiary} />
            <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
              No observations yet
            </Text>
          </View>
        )}
      </Card>
    </>
  );
}

const styles = StyleSheet.create({
  card: {},
  resolvedMarker: {
    width: 0,
    height: 0,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING.sm,
  },
  title: {
    ...TYPOGRAPHY.h4,
    fontSize: FONT_SIZES.md,
    letterSpacing: -0.1,
  },
  viewAll: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  viewAllText: {
    ...TYPOGRAPHY.label,
  },
  photoStrip: {
    flexDirection: 'row',
    gap: SPACING.sm,
    marginBottom: SPACING.smd,
  },
  photoThumb: {
    width: 72,
    height: 72,
    borderRadius: BORDER_RADIUS.md,
  },
  list: {
    marginTop: 2,
  },
  row: {
    flexDirection: 'row',
    gap: SPACING.smd,
    paddingVertical: SPACING.smd,
  },
  rowBody: {
    flex: 1,
  },
  caption: {
    ...TYPOGRAPHY.body,
    lineHeight: 18,
  },
  meta: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 3,
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING.lg,
    borderRadius: BORDER_RADIUS.md,
    gap: SPACING.xs,
  },
  emptyText: {
    ...TYPOGRAPHY.bodySmall,
  },
  // Static, not the shimmering Skeleton: the visual audit can't freeze RN
  // Animated.
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd,
    paddingVertical: SPACING.smd,
  },
  loadingThumb: {
    width: 40,
    height: 40,
    borderRadius: BORDER_RADIUS.md,
  },
  loadingLine: {
    flex: 1,
    height: 13,
    borderRadius: BORDER_RADIUS.sm,
  },
});
